import type { FlexDemoStep } from "./flexDemo.const";
import { FLEX_DEMO_PATH, FLEX_DEMO_STEPS } from "./flexDemo.const";
import { MY_CLUSTER_PATH } from "@/pages/my-cluster/myCluster.const";

export const FLEX_DEMO_EXPLORE_STEPS: readonly FlexDemoStep[] = [
  "explore",
  "preview",
  "express-interest",
  "auto-accept",
  "pay",
];

export const FLEX_DEMO_WORKSPACE_STEPS: readonly FlexDemoStep[] = ["workspace", "finish"];

export function isFlexDemoExploreStep(step: FlexDemoStep): boolean {
  return (FLEX_DEMO_EXPLORE_STEPS as readonly string[]).includes(step);
}

export function isFlexDemoWorkspaceStep(step: FlexDemoStep): boolean {
  return (FLEX_DEMO_WORKSPACE_STEPS as readonly string[]).includes(step);
}

export function flexDemoStepIndex(step: FlexDemoStep): number {
  return Math.max(0, FLEX_DEMO_STEPS.indexOf(step));
}

export type FlexDemoCoachCopy = {
  pageName: string;
  pageIsFor: string;
  nextClick: string;
  spotlight: string | null;
  /** When false, only a corner hint is shown so forms and modals stay clickable. */
  blocking?: boolean;
};

export function getFlexDemoCoachCopy(
  step: FlexDemoStep,
  collaborationsNavOpened: boolean,
  workspaceOpened = false,
): FlexDemoCoachCopy {
  if (step === "explore") {
    return {
      pageName: "Explore",
      pageIsFor: "This is where you find companies to collaborate with.",
      nextClick: "Click View company on GT Setu Demo Partner.",
      spotlight: "view-company",
    };
  }
  if (step === "preview") {
    return {
      pageName: "Company workspace",
      pageIsFor: "This is the same company profile you would open from Explore for a live partner.",
      nextClick: "Click Explore Opportunities.",
      spotlight: "express-interest-cta",
    };
  }
  if (step === "express-interest") {
    return {
      pageName: "Express interest",
      pageIsFor:
        "This is the live Express Interest form. Select products, answer the questions, generate proposals, accept the NDA, then Connect.",
      nextClick: "Fill the form in the modal, then click Connect.",
      spotlight: null,
      blocking: false,
    };
  }
  if (step === "auto-accept") {
    return {
      pageName: "Match success",
      pageIsFor: "This is the same Match Success screen after both companies accept. Next you unlock the workspace.",
      nextClick: "Click Proceed to Payment.",
      spotlight: "continue-unlock",
    };
  }
  if (step === "pay") {
    return {
      pageName: "Unlock workspace",
      pageIsFor:
        "This is the live unlock modal. In production it can charge 1 collaboration. This walkthrough is waived and does not touch your wallet.",
      nextClick: "Accept the NDA if shown, then click Proceed.",
      spotlight: null,
      blocking: false,
    };
  }
  if (step === "workspace" && !collaborationsNavOpened) {
    return {
      pageName: "Collaborations",
      pageIsFor: "Unlocked workspaces live under Collaborations in the sidebar.",
      nextClick: "Click Collaborations in the left sidebar.",
      spotlight: "nav-collaborations",
    };
  }
  if (step === "workspace" && !workspaceOpened) {
    return {
      pageName: "Your collaborations",
      pageIsFor: "This is the same Collaborations list you use after unlock. Open the demo workspace from the card.",
      nextClick: "Click Open Workspace on GT Setu Demo Partner.",
      spotlight: "open-workspace",
    };
  }
  if (step === "workspace") {
    return {
      pageName: "Collaboration workspace",
      pageIsFor: "Overview, Reports, and Workspace are the same tabs as a live match after both companies pay.",
      nextClick: "Look through the tabs, then click Finish walkthrough.",
      spotlight: "finish-walkthrough",
      blocking: false,
    };
  }
  return {
    pageName: "Finish",
    pageIsFor: "You have walked Explore → Express Interest → Collaborations workspace.",
    nextClick: "Click Enter GTsetu to use the real app.",
    spotlight: "enter-gtsetu",
  };
}

export function isFlexDemoNavAllowed(
  navPath: string,
  step: FlexDemoStep,
): boolean {
  if (navPath === "/my-discovery" || navPath === FLEX_DEMO_PATH) {
    return isFlexDemoExploreStep(step);
  }
  if (navPath === "/collaborations") {
    return isFlexDemoWorkspaceStep(step);
  }
  return false;
}

export function flexDemoNavLockedTooltip(navPath: string): string {
  if (navPath.includes("shortlist")) return "You will use Shortlist after this demo to save companies.";
  if (navPath === "/home" || navPath.endsWith("/home")) {
    return "Home is where sent and received requests appear after this demo.";
  }
  if (navPath === "/profile") return "My Profile is your company page. You will use it after this demo.";
  if (navPath === MY_CLUSTER_PATH) return "My Cluster lists ecosystems you have joined after this demo.";
  if (navPath === "/updates") return "Updates is for notifications after this demo.";
  if (navPath.startsWith("/management")) return "Management tools are not part of this walkthrough.";
  if (navPath === "/collaborations") {
    return "Collaborations opens after you unlock the demo workspace.";
  }
  if (navPath === "/my-discovery") {
    return "Stay on Explore until you unlock the workspace.";
  }
  return "You will use this after the demo.";
}
